import * as React from 'react';

import { Messages, Formatters } from '../../../codegen/Localize';
import { FlowBaseInfo } from '../../../ts/models/FlowInfo';
import { DetailsTooltipStyles } from '../CardView/DetailsTooltipStyles';
import { MarkupField } from '../Components/MarkupField';
import { BasicField } from './Elements/BasicField';
import { Name } from './Elements/Name';
import { RelatedEntity } from './Elements/RelatedEntity';
import { Tags } from './Elements/Tags';
import { Timestamp } from './Elements/Timestamp';
import { Tooltip } from './Elements/Tooltip';
import { User } from './Elements/User';
import { ContentTooltipProps, getProjectLink, TooltipDescriptionTestId } from './TooltipHelper';
import { ContentDraftStatus } from '../../../ts/ServerApi/types/ContentDraft';
import { TooltipDraftIndicator } from '../../../drafts/components/TooltipDraftIndicator';
import { appStore } from '../../AppStore';
import { editFlow } from '../../../flow/actions/EditFlowReduxAction';
import { ServiceLocator } from '../../ServiceLocator';
import { Flows } from '../../../ts/resources/Flows';
import { FlowDraftItemInfo } from '../../../ts/models/FlowDraftInfo';
import { getFlowDraftId } from '../../../drafts/utils/DraftUtils';
import { ServerService } from '../../../ts/services/ServerService';

export function FlowTooltip(props: ContentTooltipProps): JSX.Element {
  const { content, urls } = props;
  const flowInfo = content as FlowBaseInfo;
  const [draftLastEdited, setDraftLastEdited] = React.useState('');
  const [hideEditingLink, setHideEditingLink] = React.useState(false);
  React.useEffect(() => {
    const draftId = getFlowDraftId(flowInfo);
    if (flowInfo.draftStatus !== ContentDraftStatus.none && draftId) {
      const flows = ServiceLocator.get<Flows>('Flows');
      flows.getDraft(draftId.toString()).then((result) => {
        const lastUpdatedAt = (result as unknown as FlowDraftItemInfo).lastUpdatedAt;
        const lastUpdatedAtFormatted: string = Formatters.formatLongBestFitRelativeTime(new Date(lastUpdatedAt));
        setDraftLastEdited(Messages.draftLastEditIndicatorText({LAST_EDITED: lastUpdatedAtFormatted}));

        if (flowInfo.draftStatus === ContentDraftStatus.unpublished) {
          const server = ServiceLocator.get<ServerService>('ServerService');
          if ((result as any).ownerId !== server.sessionUserId()) {
            setHideEditingLink(true);
          }
        }
      });
    }
  },
  [props.content.draftId]
  );

  const handleEditFlow = () => {
    if (flowInfo) {
      appStore.dispatch(editFlow(flowInfo));
    }
  };

  return (
    <Tooltip>
      <Name name={content.name} />
      <MarkupField markup={props.content.description}
                  testId={TooltipDescriptionTestId}
                  extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={props.content.tags} />
      <BasicField name={Messages.inputSteps()} value={flowInfo.inputCount} />
      <BasicField name={Messages.outputSteps()} value={flowInfo.outputCount} />
      {!props.inProjectSpace && <RelatedEntity label={Messages.project()} link={getProjectLink(flowInfo.project, urls)} /> }
      <User user={props.content.owner} label={Messages.owner()} urls={urls} />
      <Timestamp label={Messages.modified()} value={props.content.updatedAt} />
      {flowInfo.draftStatus !== ContentDraftStatus.none &&
        <TooltipDraftIndicator
          indicatorText={() => draftLastEdited}
          linkText={Messages.continueEditing}
          handleAction={handleEditFlow}
          hideEditingLink={hideEditingLink}
        />}
    </Tooltip>
  );
}
